import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {buildYearOptions, MONTH_OPTIONS} from './profileFormOptions';
import {FormSection} from '../../../../../../components/FormMaker/types';
import {ApplicantProfile} from '../../../types';

export const useWorkExperienceSection = (profile: ApplicantProfile, editId?: string): FormSection[] => {
  const {t: tfw} = useTranslation('translation', {keyPrefix: 'applicantProfile.form.workExperience'});
  const {t: tfsh} = useTranslation('translation', {keyPrefix: 'applicantProfile.form.shared'});
  const {t: tsh} = useTranslation('translation', {keyPrefix: 'shared'});

  const we = useMemo(() => profile.workExperience?.find((w) => w.id === editId), [profile.workExperience, editId]);
  const years = useMemo(() => buildYearOptions(), []);

  return useMemo<FormSection[]>(
    () => [
      {
        type: 'title',
        data: {
          title: tfw('title'),
        },
      },
      {
        type: 'text',
        data: {
          name: 'organizationName',
          placeholder: tfw('organizationName.placeholder'),
          label: tfw('organizationName.label'),
          type: 'text',
          initialValue: we?.organizationName,
          schema: Yup.string()
            .min(2, 'Too Short!')
            .max(64, 'Too Long!')
            .required(tsh('formValidation.required', {fieldName: tfw('organizationName.label')}) || 'Required'),
        },
      },
      {
        type: 'text',
        data: {
          name: 'location',
          placeholder: tfw('location.placeholder'),
          label: tfw('location.label'),
          type: 'text',
          initialValue: we?.location,
          schema: Yup.string()
            .min(2, 'Too Short!')
            .max(64, 'Too Long!')
            .required(tsh('formValidation.required', {fieldName: tfw('location.label')}) || 'Required'),
        },
      },
      {
        type: 'subtitle',
        data: {
          subtitle: tfw('position.subtitle'),
        },
      },
      {
        type: 'text',
        data: {
          name: 'roleName',
          placeholder: tfw('position.title.placeholder'),
          label: tfw('position.title.label'),
          type: 'text',
          initialValue: we?.roleName,
          schema: Yup.string()
            .min(2, 'Too Short!')
            .max(64, 'Too Long!')
            .required(tsh('formValidation.required', {fieldName: tfw('position.title.label')}) || 'Required'),
        },
      },
      {
        type: 'checkbox',
        data: {
          name: 'isCurrent',
          label: tfw('position.isCurrent.label'),
          initialValue: !!we?.isCurrent,
        },
      },
      {
        type: 'label',
        data: {
          label: tfsh('dates.startDate'),
        },
      },
      {
        type: 'stacked',
        data: {
          sections: [
            {
              type: 'select',
              data: {
                name: 'startMonth',
                placeholder: tfsh('dates.month'),
                options: MONTH_OPTIONS,
                initialValue: we?.startMonth,
                schema: Yup.number()
                  .min(1)
                  .max(12)
                  .required(tsh('formValidation.required', {fieldName: tfsh('dates.month')}) || 'Required'),
              },
            },
            {
              type: 'select',
              data: {
                name: 'startYear',
                placeholder: tfsh('dates.year'),
                options: years,
                initialValue: we?.startYear,
                schema: Yup.number()
                  .min(1950)
                  .max(new Date().getFullYear())
                  .required(tsh('formValidation.required', {fieldName: tfsh('dates.year')}) || 'Required'),
              },
            },
          ],
        },
      },
      {
        type: 'label',
        data: {
          label: tfsh('dates.endDate'),
        },
      },
      {
        type: 'stacked',
        data: {
          sections: [
            {
              type: 'select',
              data: {
                name: 'endMonth',
                placeholder: tfsh('dates.month'),
                options: MONTH_OPTIONS,
                initialValue: we?.endMonth,
                schema: Yup.number()
                  .min(1)
                  .max(12)
                  .when(['startYear', 'endYear'], ([startYear, endYear], schema) => {
                    if (startYear === endYear) {
                      return schema.min(Yup.ref('startMonth'), tfsh('dates.endMonthInvalid') || 'Required');
                    }
                    return schema;
                  })
                  .when('isCurrent', ([isCurrent], schema) =>
                    isCurrent
                      ? schema.nullable()
                      : schema.required(tsh('formValidation.required', {fieldName: tfsh('dates.month')}) || 'Required')
                  ),
              },
            },
            {
              type: 'select',
              data: {
                name: 'endYear',
                placeholder: tfsh('dates.year'),
                options: years,
                initialValue: we?.endYear,
                schema: Yup.number()
                  .min(1950)
                  .min(Yup.ref('startYear'), tfsh('dates.endYearInvalid') || 'Required')
                  .max(new Date().getFullYear())
                  .when('isCurrent', ([isCurrent], schema) =>
                    isCurrent
                      ? schema.nullable()
                      : schema.required(tsh('formValidation.required', {fieldName: tfsh('dates.year')}) || 'Required')
                  ),
              },
            },
          ],
        },
        hideCondition: (values: any) => values.isCurrent,
      },
      // NOTE: when isCurrent is true, show dummy disabled end date fields
      {
        type: 'stacked',
        data: {
          sections: [
            {
              type: 'select',
              data: {
                name: 'endMonthDummy',
                placeholder: tfsh('dates.month'),
                options: [],
                disabled: true,
                initialValue: null,
              },
            },
            {
              type: 'select',
              data: {
                name: 'endYearDummy',
                placeholder: tfsh('dates.year'),
                options: [],
                disabled: true,
                initialValue: null,
              },
            },
          ],
        },
        hideCondition: (values: any) => !values.isCurrent,
      },
      {
        type: 'textarea',
        data: {
          name: 'description',
          placeholder: tfw('description.placeholder'),
          label: tfw('description.label'),
          initialValue: we?.description || '',
          schema: Yup.string().min(2, 'Too Short!').max(2000, 'Too Long!'),
        },
      },
    ],
    [tfsh, tfw, tsh, we, years]
  );
};
